import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, PencilSimple, Trash, SpinnerGap } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorMatrixPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    company: '',
    vendor: '',
    end_client: '',
    discount_percent: 0,
    volume_discount: 0,
    tenure_discount: 0,
    ach_fee: 0,
    portal_fee: 0,
    msp_fee: 0,
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vendor-matrix`, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (entry = null) => {
    if (entry) {
      setEditEntry(entry);
      setFormData(entry);
    } else {
      setEditEntry(null);
      setFormData({
        company: '',
        vendor: '',
        end_client: '',
        discount_percent: 0,
        volume_discount: 0,
        tenure_discount: 0,
        ach_fee: 0,
        portal_fee: 0,
        msp_fee: 0,
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editEntry) {
        await axios.put(`${API_URL}/api/admin/vendor-matrix/${editEntry.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/vendor-matrix`, formData, { withCredentials: true });
      }
      setShowModal(false);
      fetchEntries();
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/vendor-matrix/${id}`, { withCredentials: true });
      fetchEntries();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const calculateFBR = (entry) => {
    const parseNum = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const totalDiscount = parseNum(entry.discount_percent) + parseNum(entry.ach_fee) + parseNum(entry.portal_fee) + parseNum(entry.msp_fee);
    return `${(100 - totalDiscount).toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
            Vendor & Client Matrix
          </h1>
          <p className="text-slate-500 mt-1">Configure discount rates for vendors and clients. Discounts are automatically applied when a matching vendor is selected during placement creation.</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleOpenModal()} data-testid="add-vendor-btn">
          <Plus size={20} className="mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            <strong>FBR Calculation:</strong> Final Bill Rate = Bill Rate × (1 - [Sum of all fees/discounts])
          </p>
        </CardContent>
      </Card>

      {/* Matrix Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <p>No vendor/client entries configured yet</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenModal()}>
                Add Your First Entry
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3">Organization</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">End Client</th>
                    <th className="px-4 py-3 text-right">Discount %</th>
                    <th className="px-4 py-3 text-right">Volume</th>
                    <th className="px-4 py-3 text-right">Tenure</th>
                    <th className="px-4 py-3 text-right">ACH Fee</th>
                    <th className="px-4 py-3 text-right">Portal Fee</th>
                    <th className="px-4 py-3 text-right">MSP Fee</th>
                    <th className="px-4 py-3 text-right">FBR Factor</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="font-medium">{entry.company}</td>
                      <td>{entry.vendor}</td>
                      <td>{entry.end_client}</td>
                      <td className="text-right">{entry.discount_percent}%</td>
                      <td className="text-right text-xs">{typeof entry.volume_discount === 'string' ? entry.volume_discount : `${entry.volume_discount || 0}%`}</td>
                      <td className="text-right text-xs">{typeof entry.tenure_discount === 'string' ? entry.tenure_discount : `${entry.tenure_discount || 0}%`}</td>
                      <td className="text-right">{entry.ach_fee}%</td>
                      <td className="text-right">{entry.portal_fee}%</td>
                      <td className="text-right">{entry.msp_fee}%</td>
                      <td className="text-right font-medium text-emerald-600">{calculateFBR(entry)}</td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenModal(entry)}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(entry.id)}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editEntry ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Client *</Label>
                <Input
                  value={formData.end_client}
                  onChange={(e) => setFormData({ ...formData, end_client: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'discount_percent', label: 'Discount %' },
                { key: 'volume_discount', label: 'Volume Discount %' },
                { key: 'tenure_discount', label: 'Tenure Discount %' },
                { key: 'ach_fee', label: 'ACH Fee %' },
                { key: 'portal_fee', label: 'Portal Fee %' },
                { key: 'msp_fee', label: 'MSP Fee %' },
              ].map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData[field.key]}
                    onChange={(e) => setFormData({ ...formData, [field.key]: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : null}
                {editEntry ? 'Update' : 'Add'} Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorMatrixPage;
